import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { OrdersListTableRow } from '../orders-list-table/orders-list-table.component';
import { OrderRowAction, buildOrderRowActions } from '../../utils/order-row-actions.util';

/**
 * Shared row-actions menu for admin/staff orders pages.
 * Keeps the large SVG markup in one place for CodeAnt duplicate-density.
 *
 * CWS-1779: the items come from `buildOrderRowActions`, which reads the
 * server's per-row cancellation flags (CWS-1771). The host's own `canCancelOrder`
 * (role, ownership) is applied on top; a row the server has not decided falls
 * back to today's View + Cancel.
 */
@Component({
  selector: 'app-orders-actions-dropdown',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './orders-actions-dropdown.component.html',
})
export class OrdersActionsDropdownComponent {
  @Input() openDropdownId: string | null = null;
  @Input() selectedOrder: OrdersListTableRow | null = null;
  /** When true, Cancel is always shown (disabled when not cancellable). Admin style. */
  @Input() alwaysShowCancel = false;
  @Input({ required: true }) canCancelOrder!: (order: OrdersListTableRow) => boolean;

  @Output() view = new EventEmitter<OrdersListTableRow>();
  @Output() cancel = new EventEmitter<OrdersListTableRow>();
  /** CWS-1779: refund a completed item. Opens the cancel & refund panel (CWS-1778). */
  @Output() refund = new EventEmitter<OrdersListTableRow>();
  /** CWS-1779: retry the refund on a cancellation that still owes money. */
  @Output() retryRefund = new EventEmitter<OrdersListTableRow>();

  get actions(): OrderRowAction[] {
    const order = this.selectedOrder;
    if (!order) return [];
    return buildOrderRowActions({
      washTypeValue: order.washTypeValue,
      addOnLabels: order.addOnLabels,
      cancellation: order.cancellation,
      canCancel: this.canCancelOrder(order),
      alwaysShowCancel: this.alwaysShowCancel,
    });
  }

  trackAction(_index: number, action: OrderRowAction): string {
    return action.key;
  }

  onAction(action: OrderRowAction): void {
    const order = this.selectedOrder;
    if (!order || !action.enabled) return;
    if (action.key === 'view') this.view.emit(order);
    else if (action.key === 'cancel') this.cancel.emit(order);
    else if (action.key === 'retry_refund') this.retryRefund.emit(order);
    else this.refund.emit(order);
  }
}
